import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import tradeShowService from '../../services/tradeShowService';
import TrendBarChart from '../../components/charts/TrendBarChart';

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = [CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2];

export default function TradeShowAnalyticsPage() {
  const navigate = useNavigate();
  const [year, setYear] = useState(CURRENT_YEAR);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    tradeShowService.getYearSummary(year).then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [year]);

  const sorted = useMemo(() => (data?.shows || []).slice().sort((a, b) => (b.roiPct ?? -Infinity) - (a.roiPct ?? -Infinity)), [data]);

  return (
    <div className="container mt-4" style={{ maxWidth: 1000 }}>
      <div className="d-flex align-items-center gap-2 mb-1">
        <button className="btn btn-link p-0 text-muted" onClick={() => navigate('/trade-shows')}>← Trade Shows</button>
      </div>
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h4 className="mb-0">Year-End Trade Show Report</h4>
        <select className="form-select form-select-sm" style={{ width: 120 }} value={year} onChange={e => setYear(parseInt(e.target.value, 10))}>
          {YEAR_OPTIONS.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="text-center py-5"><span className="spinner-border" /></div>
      ) : !data || data.shows.length === 0 ? (
        <p className="text-muted fst-italic">No trade shows in {year}.</p>
      ) : (
        <>
          <div className="row g-3 mb-4">
            <div className="col-md-3">
              <div className="card text-center"><div className="card-body py-3">
                <div className="text-muted text-uppercase" style={{ fontSize: 11 }}>Total Spend</div>
                <div className="fw-bold" style={{ fontSize: 22 }}>{money(data.totals.totalCost)}</div>
              </div></div>
            </div>
            <div className="col-md-3">
              <div className="card text-center"><div className="card-body py-3">
                <div className="text-muted text-uppercase" style={{ fontSize: 11 }}>Revenue Generated</div>
                <div className="fw-bold text-success" style={{ fontSize: 22 }}>{money(data.totals.revenueGenerated)}</div>
              </div></div>
            </div>
            <div className="col-md-3">
              <div className="card text-center"><div className="card-body py-3">
                <div className="text-muted text-uppercase" style={{ fontSize: 11 }}>Leads Captured</div>
                <div className="fw-bold" style={{ fontSize: 22 }}>{data.totals.leadsCaptured}</div>
              </div></div>
            </div>
            <div className="col-md-3">
              <div className="card text-center"><div className="card-body py-3">
                <div className="text-muted text-uppercase" style={{ fontSize: 11 }}>Converted</div>
                <div className="fw-bold" style={{ fontSize: 22 }}>{data.totals.leadsConverted}</div>
              </div></div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-header" style={{ backgroundColor: '#1b2638', color: 'white' }}>ROI by Show — {year}</div>
            <div className="card-body">
              <TrendBarChart
                labels={sorted.map(s => s.name)}
                values={sorted.map(s => Math.round(s.roiPct ?? 0))}
                yAxisTitle="ROI (%)"
                colors={sorted.map(s => (s.roiPct ?? 0) >= 0 ? '#198754' : '#dc3545')}
              />
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-header" style={{ backgroundColor: '#1b2638', color: 'white' }}>Revenue by Business Type — {year}</div>
            <div className="card-body">
              <TrendBarChart
                labels={['Retail', 'Food Service', 'Unspecified']}
                values={[data.totals.revenueByChannel.retail, data.totals.revenueByChannel.food_service, data.totals.revenueByChannel.unspecified]}
                yAxisTitle="Revenue ($)"
                colors={['#0d6efd', '#fd7e14', '#adb5bd']}
              />
            </div>
          </div>

          <div className="table-responsive">
            <table className="table table-sm table-hover align-middle">
              <thead className="table-light">
                <tr>
                  <th>Show</th><th className="text-end">Cost</th><th className="text-end">Leads</th>
                  <th className="text-end">Converted</th><th className="text-end">Revenue</th><th className="text-end">ROI</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(s => (
                  <tr key={s.tradeShowId} style={{ cursor: 'pointer' }} onClick={() => navigate(`/trade-shows/${s.tradeShowId}`)}>
                    <td className="fw-semibold small">{s.name}</td>
                    <td className="text-end">{money(s.totalCost)}</td>
                    <td className="text-end">{s.leadsCaptured}</td>
                    <td className="text-end">{s.leadsConverted}</td>
                    <td className="text-end">{money(s.revenueGenerated)}</td>
                    <td className="text-end">
                      {s.roiPct == null ? '—' : (
                        <span className={`badge ${s.roiPct >= 0 ? 'bg-success' : 'bg-danger'}`}>{s.roiPct >= 0 ? '+' : ''}{s.roiPct.toFixed(0)}%</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
